import { getNotificationPermission } from './firebase';
import axios from 'axios';
import { url } from '../client_config/config';

/**
 * Notification Permission Service
 * Handles the smart notification permission flow including:
 * - User preference tracking
 * - Smart timing for showing prompts
 * - Browser permission checks
 * - Backend sync
 */

const PREF_KEYS = {
  GRANTED: 'granted',
  DENIED: 'denied',
  DONT_ASK: 'dont_ask',
  ASK_ME_LATER: 'ask_me_later'
};

export function getUserPushNotificationEnabled(user) {
  if (!user) {
    return false;
  }

  if (user.userType === 'performer') {
    // Schema default is true; treat missing as enabled (matches server !== false)
    return user.performer?.settings?.notifications?.push !== false;
  }

  if (user.userType === 'admin') {
    return user.company?.settings?.notifications?.push !== false;
  }

  if (user.userType === 'judge') {
    return user.judge?.preferences?.notificationPreferences?.push !== false;
  }

  return user.settings?.notifications?.push !== false;
}

/**
 * Check if we should show the notification permission modal.
 * Preference-on alone is NOT enough — schema defaults push to true without an FCM token.
 * Only skip when this device has OS permission + a live subscribed token (or user opted out).
 *
 * @param {Object} userPreference - User's stored notification preference
 * @param {Object} user - Auth user
 * @param {{ isSubscribed?: boolean }} [options]
 * @returns {boolean} - Whether to show the modal
 */
export function shouldShowNotificationModal(userPreference, user, options = {}) {
  const browserPermission = getNotificationPermission();
  const hasLiveToken = Boolean(
    options.isSubscribed ||
    (typeof localStorage !== 'undefined' && localStorage.getItem('fcmToken'))
  );

  // Fully enabled on this device: OS permission + live FCM token
  if (browserPermission === 'granted' && hasLiveToken) {
    return false;
  }

  // Don't show if user said don't ask again
  if (userPreference?.preference === PREF_KEYS.DONT_ASK) {
    return false;
  }

  // Don't show if browser permanently denied
  if (browserPermission === 'denied') {
    return false;
  }

  // Explicit opt-out of push preference — don't nag
  if (user && getUserPushNotificationEnabled(user) === false) {
    return false;
  }

  // Don't show if recently deferred (within 7 days)
  if (userPreference?.updatedAt) {
    const lastShown = new Date(userPreference.updatedAt);
    const daysSinceLastShown = (Date.now() - lastShown.getTime()) / (1000 * 60 * 60 * 24);

    if (userPreference.preference === PREF_KEYS.ASK_ME_LATER && daysSinceLastShown < 7) {
      return false;
    }
  }

  // OK to show modal (includes preference-on-but-never-subscribed)
  return true;
}

/**
 * Get smart timing delay for showing notification modal
 * @returns {number} - Delay in milliseconds
 */
export function getNotificationModalDelay() {
  // Show immediately after login
  return 1000; // 1 second delay for smooth transition
}

/**
 * Update user notification preference on backend
 * @param {string} preference - The preference to save
 * @returns {Promise<Object>} - Response from backend
 */
export async function updateNotificationPreference(preference) {
  try {
    const response = await axios.put(`${url}/users/notification-preference`, {
      preference
    }, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to update notification preference:', error);
    throw error;
  }
}

/**
 * Get user notification preference from backend
 * @returns {Promise<Object>} - User's notification preference
 */
export async function getNotificationPreference() {
  try {
    const response = await axios.get(`${url}/users/notification-preference`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    });
    return response.data.notificationPreference;
  } catch (error) {
    console.error('Failed to get notification preference:', error);
    return null;
  }
}

/**
 * Register FCM token with backend
 * @param {string} token - FCM token to register
 * @returns {Promise<Object>} - Response from backend
 */
export async function registerFCMToken(token) {
  try {
    const subscribeUrl = `${url}/push-notifications/subscribe`;
    console.debug('[PUSH_DEBUG] Registering FCM token', {
      subscribeUrl,
      hasAuthToken: Boolean(localStorage.getItem('token')),
      tokenLength: token?.length || 0
    });
    const response = await axios.post(subscribeUrl, {
      fcmToken: token,
      deviceInfo: navigator.userAgent || 'Unknown Device'
    }, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    });
    console.log('[PUSH_DEBUG] FCM token registered with backend', {
      status: response.status,
      success: response.data?.success
    });
    return response.data;
  } catch (error) {
    console.error('[PUSH_DEBUG] Failed to register FCM token:', {
      apiUrl: `${url}/push-notifications/subscribe`,
      status: error.response?.status,
      message: error.response?.data?.error || error.message
    });
    throw error;
  }
}

export async function updatePushNotificationSetting(userType, enabled) {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('token')}`
  };

  if (userType === 'performer') {
    const response = await axios.put(`${url}/users/performer-settings`, {
      performer: {
        settings: {
          notifications: {
            push: enabled
          }
        }
      }
    }, { headers });

    return response.data;
  }

  if (userType === 'admin') {
    const response = await axios.put(`${url}/users/company-settings`, {
      settings: {
        notifications: {
          push: enabled
        }
      }
    }, { headers });

    return response.data;
  }

  if (userType === 'judge') {
    const response = await axios.put(`${url}/users/judge-profile`, {
      judge: {
        preferences: {
          notificationPreferences: {
            push: enabled
          }
        }
      }
    }, { headers });

    return response.data;
  }

  return null;
}

export { PREF_KEYS };
